import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';

import {RoutineSchemaSource} from './eventTriggerConfigForm/routineSchemaSources';
import {getTitleCasedObjectTypeLabel, toCommonNounLabel} from './routineUtils';

interface ObjectTypeUIConfig {
  // Overrides the data type name shown in the UI. When unset, the name is derived from the
  // EVENT_TRIGGER_REGISTRY key (e.g. "CustomEntity" → "Custom Entity"). Set this for types whose
  // derived name would be incorrect, such as acronyms (e.g. "DnaSequence" → "DNA Sequence").
  label?: string;
  pluralLabel: string;
  searchType: string;
  // The schema source determines which query backs the schema selector for this object type. Most types
  // use tag schemas (`oligoAwareTagSchema`); non-tag types declare their own source (e.g. `studySchema`).
  schemaSource?: RoutineSchemaSource;
}

function oligoAwareTagSchemaSource(folderItemType: OligoAwareFolderItemType): RoutineSchemaSource {
  return {kind: 'oligoAwareTagSchema', folderItemType};
}

// TODO (BNCH-154854): Centralize this configuration in one place or make it dynamic.
// Keys must match the PascalCase object types from EVENT_TRIGGER_REGISTRY on the backend.
const OBJECT_TYPE_UI_CONFIG: Record<string, ObjectTypeUIConfig> = {
  Analysis: {pluralLabel: 'analyses', searchType: ModelTypeConstants.ANALYSIS},
  CustomEntity: {
    pluralLabel: 'custom entities',
    searchType: ModelTypeConstants.CUSTOM_ENTITY,
    schemaSource: oligoAwareTagSchemaSource(ModelTypeConstants.BASIC_FOLDER_ITEM),
  },
  DnaSequence: {
    label: 'DNA Sequence',
    pluralLabel: 'DNA sequences',
    searchType: ModelTypeConstants.SEQUENCE,
    schemaSource: oligoAwareTagSchemaSource(ModelTypeConstants.SEQUENCE),
  },
  Entry: {
    pluralLabel: 'entries',
    searchType: ModelTypeConstants.ENTRY,
    schemaSource: oligoAwareTagSchemaSource(ModelTypeConstants.ENTRY),
  },
  Mixture: {
    pluralLabel: 'mixtures',
    searchType: ModelTypeConstants.MIXTURE,
    schemaSource: oligoAwareTagSchemaSource(ModelTypeConstants.MIXTURE),
  },
  Molecule: {
    pluralLabel: 'molecules',
    searchType: ModelTypeConstants.MOLECULE,
    schemaSource: oligoAwareTagSchemaSource(ModelTypeConstants.MOLECULE),
  },
  Protein: {
    pluralLabel: 'proteins',
    searchType: ModelTypeConstants.PROTEIN,
    schemaSource: oligoAwareTagSchemaSource(ModelTypeConstants.PROTEIN),
  },
  RnaSequence: {
    label: 'RNA Sequence',
    pluralLabel: 'RNA sequences',
    searchType: ModelTypeConstants.RNA_SEQUENCE,
    schemaSource: oligoAwareTagSchemaSource(ModelTypeConstants.RNA_SEQUENCE),
  },
  Study: {
    pluralLabel: 'studies',
    searchType: ModelTypeConstants.STUDY,
    schemaSource: {kind: 'studySchema'},
  },
};

export function getObjectTypeUIConfig(objectType: string): ObjectTypeUIConfig | undefined {
  return OBJECT_TYPE_UI_CONFIG[objectType];
}

export function getModelTypeConstantForObjectType(objectType: string): string | null {
  return getObjectTypeUIConfig(objectType)?.searchType ?? null;
}

// Prefer the explicit UI label override when set; otherwise derive the name from the registry key.
export function getObjectTypeDisplayLabel(objectType: string): string {
  return getObjectTypeUIConfig(objectType)?.label ?? getTitleCasedObjectTypeLabel(objectType);
}

export function getCommonNounObjectTypeLabel(objectType: string): string {
  return toCommonNounLabel(getObjectTypeDisplayLabel(objectType));
}
